// TODO This violates our eslint rule for no-console.  This should be removed or updated in eslint as it's allowing caller functions to bypass it
/* eslint-disable no-console */

// Utils
import { getGlobalLogLevel, getChannelLogLevel } from './loggerUtils';
import { sanitizeLogValue } from './sanitizeLogValue';
import { forwardWarnOrErrorToRollbar } from './loggerRollbarForwarder';
import isInSampleRate from '../../common/utils/lib/sampleRate';

// Constants
import { LoggerLevel } from './loggerConstants';

// Internal symbol to mark already sanitized structures to avoid double processing.
const SANITIZED_SYMBOL: unique symbol = Symbol('sanitizedLogValue');

interface PossiblySanitized {
    [SANITIZED_SYMBOL]?: boolean;
}

type LoggerOptions = {
    autoSanitize?: boolean;
    /**
     * When true, logs at this level will be output to the console but not forwarded to external log services, even if
     * they meet the log level threshold. This is to control the number of events sent to external logging services.
     *
     * WARNING: This does not guarantee they will not end up in external services, so caution should still be used to
     * avoid logging sensitive data. For example, when a log is forwarded to Rollbar, Rollbar's own client-side SDK may
     * have additional context added to the payload, such as other recent console logs.
     */
    isConsoleOnly?: boolean;
};

/**
 * Marks sanitized values so repeated logger forwarding paths do not redact the same object twice.
 */
const markSanitized = <T>(value: T): T => {
    if (value && typeof value === 'object') {
        try {
            (value as PossiblySanitized)[SANITIZED_SYMBOL] = true;
        } catch {
            // ignore non-writable objects
        }
    }
    return value;
};

/**
 * Checks whether a value has already been processed by the logger sanitizer.
 */
const isAlreadySanitized = (value: unknown): boolean =>
    !!(value && typeof value === 'object' && (value as PossiblySanitized)[SANITIZED_SYMBOL]);

/**
 * Sanitizes a logger argument unless it has already been prepared by this logger.
 */
const sanitizeLoggerArgument = (value: unknown): unknown => {
    if (isAlreadySanitized(value)) return value;
    return markSanitized(sanitizeLogValue(value));
};

/**
 * Sanitizes dynamic logger arguments while preserving the first Error for native stack rendering.
 */
const prepareLoggerArguments = (values: unknown[]): unknown[] => {
    let hasPreservedTopLevelError = false;

    for (let i = 1; i < values.length; i += 1) {
        const value = values[i];

        if (!hasPreservedTopLevelError && value instanceof Error) {
            // Intentionally preserve the first top-level Error so console and Rollbar render the real exception stack.
            hasPreservedTopLevelError = true;
            continue;
        }

        values[i] = sanitizeLoggerArgument(value);
    }

    return values;
};

// We forward both WARN and ERROR as per loggerRollbarForwarder logic
const ROLLBAR_LOG_LEVEL = LoggerLevel.WARN;

/**
 * Compares a log event level against the configured output threshold.
 */
const isLoggableLevel = (logLevel: LoggerLevel, logLevelConfig: LoggerLevel): boolean => logLevel <= logLevelConfig;

/**
 * Checks whether a log event should be emitted to the console for the active debug level.
 */
const canLogToConsole = (logLevel: LoggerLevel, debugLevel: LoggerLevel): boolean =>
    isLoggableLevel(logLevel, debugLevel);

/**
 * Checks whether a sanitized log event should be forwarded to Rollbar.
 */
const canForwardLogToRollbar = (logLevel: LoggerLevel, isConsoleOnly: boolean, rollbarSampleRate: number): boolean => {
    if (isConsoleOnly) return false;
    if (!isLoggableLevel(logLevel, ROLLBAR_LOG_LEVEL)) return false;

    return isInSampleRate(rollbarSampleRate);
};

type RollbarSampleRateMetadata = {
    rollbarSampleRate?: unknown;
};

/**
 * Detects logger metadata objects that can control Rollbar forwarding.
 */
const hasRollbarSampleRateMetadata = (value: unknown): value is RollbarSampleRateMetadata =>
    !!(value && typeof value === 'object' && !Array.isArray(value) && 'rollbarSampleRate' in value);

/**
 * Finds the first numeric Rollbar sample rate in the logger arguments.
 */
const getRollbarSampleRate = (values: unknown[]): number => {
    const metadata = values.find(hasRollbarSampleRateMetadata);
    if (typeof metadata?.rollbarSampleRate !== 'number') return 1;

    return metadata.rollbarSampleRate;
};

class MilanoteLogger {
    channelName?: string;

    constructor(channelName?: string) {
        this.channelName = channelName;
    }

    /**
     * Creates a child logger that prefixes messages with a hierarchical channel name.
     */
    createChannel = (subChannelName: string) => {
        return new MilanoteLogger(this.channelName ? `${this.channelName}.${subChannelName}` : subChannelName);
    };

    /**
     * Builds a level-specific console logger with shared sanitization and Rollbar forwarding.
     */
    consoleLog =
        (logLevel: LoggerLevel, logMethod: (...args: unknown[]) => void, opts: LoggerOptions = {}) =>
        (...values: unknown[]) => {
            const debugLevel = this.channelName ? getChannelLogLevel(this.channelName) : getGlobalLogLevel();
            if (!canLogToConsole(logLevel, debugLevel)) return;

            // Ensure we always have a message placeholder
            if (values.length === 0) values.push('');

            if (this.channelName) values[0] = `[${this.channelName}] ${values[0]}`;

            const { autoSanitize = true, isConsoleOnly = false } = opts;

            if (autoSanitize) {
                prepareLoggerArguments(values);
            }

            logMethod.apply(console, values);

            const rollbarSampleRate = getRollbarSampleRate(values);

            // Forward warn and error to Rollbar
            if (autoSanitize && canForwardLogToRollbar(logLevel, isConsoleOnly, rollbarSampleRate)) {
                forwardWarnOrErrorToRollbar(logLevel, values);
            }
        };

    // Expose debug logging only in development builds to avoid any chance of leaking
    // verbose diagnostic data (even though we disable autoSanitize for message-first usage).
    // In non-development environments this becomes a no-op function.
    debug =
        process.env.NODE_ENV === 'development'
            ? this.consoleLog(LoggerLevel.FINE, console.log, { autoSanitize: false, isConsoleOnly: true })
            : // Accept any args for call-site compatibility; intentionally do nothing.
              () => {};
    log = this.consoleLog(LoggerLevel.FINE, console.log);
    info = this.consoleLog(LoggerLevel.INFO, console.info);
    table = this.consoleLog(
        LoggerLevel.INFO,
        // Wrap to normalize signature for TypeScript
        (...args: unknown[]) => (console.table as unknown as (...a: unknown[]) => void)(...args),
    );
    // warn/error auto-sanitized
    error = this.consoleLog(LoggerLevel.ERROR, console.error);
    warn = this.consoleLog(LoggerLevel.WARN, console.warn);
    groupCollapsed = this.consoleLog(LoggerLevel.FINE, console.groupCollapsed, { isConsoleOnly: true });
    group = this.consoleLog(LoggerLevel.FINE, console.group, { isConsoleOnly: true });
    groupEnd = this.consoleLog(LoggerLevel.FINE, console.groupEnd, { isConsoleOnly: true });
    debugGroupCollapsed = this.consoleLog(LoggerLevel.FINE, console.groupCollapsed, { isConsoleOnly: true });
    debugGroup = this.consoleLog(LoggerLevel.FINE, console.group, { isConsoleOnly: true });
    debugGroupEnd = this.consoleLog(LoggerLevel.FINE, console.groupEnd, { isConsoleOnly: true });
}

const globalLogger = new MilanoteLogger();

export default globalLogger;
